import React, { useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import ActionMenu from '../../components/ActionMenu/ActionMenu';
import ActionMenuItem from '../../components/ActionMenu/ActionMenuItem';
import Authorize from '../../components/AuthComponent/Authorize';
import SaveButton from '../../components/Buttons/SaveButton';
import ButtonBar from '../../components/Fields/ButtonBar';
import CheckboxField from '../../components/Fields/CheckboxField';
import DateControl from '../../components/Fields/DateControl';
import Field from '../../components/Fields/Field';
import FieldLabel from '../../components/Fields/FieldLabel';
import Form from '../../components/Fields/Form';
import MultiSelectField from '../../components/Fields/MultiSelectField';
import SelectListField from '../../components/Fields/SelectListField';
import SwitchField from '../../components/Fields/SwitchField';
import TextboxField from '../../components/Fields/TextboxField';
import { FormConfig } from '../../components/Fields/Types';
import useSelectListOptions from '../../components/Hooks/SelectListOptionsHook/useSelectListOptions';
import useValidation from '../../components/Hooks/ValidationHook/useValidation';
import SpinnerOverlay from '../../components/Spinner/SpinnerOverlay';
import { HorizontalTabs } from '../../components/Tabs/HorizontalTabs';
import { ApplicationFunctions, useAuth } from '../../contexts/authContext';
import { useNotifications } from '../../contexts/notificationContext';
import WorkflowDefinitionModel from '../../models/WorkflowDefinitionModel';
import DataService from '../../services/data.service';
import IdParams from '../../types/IdParams';
import { DataResult } from '../../types/Result';
import FormatUtils from '../../utils/FormatUtils';
import Unauthorized from '../Authentication/Unauthorized';
import Workflow_tab_EventCodeTasks from './Tabs/Workflow_tab_EventCodeTasks';
import Workflow_tab_Steps from './Tabs/Workflow_tab_Steps';

const WorkflowDefinitionView: React.FC = () => {
    const projectTypes = useSelectListOptions('api/v1/selectlist/ProjectTypes');
    const itemTypes = useSelectListOptions('api/v1/selectlist/ItemTypes');
    const [serverErrors, setServerErrors] = useState<DataResult<WorkflowDefinitionModel>>({ isSuccesful: true, messages: [], data: undefined });
    const { handleResult, handleException } = useNotifications();
    const [isAuthorized, setIsAuthorized] = useState<boolean>(true);

    const [formData, setFormData] = useState<WorkflowDefinitionModel>({
        id: 0,
        name: '',
        itemTypeIds: [],
        projectTypeIds: [],
        beginDate: new FormatUtils().formatDateForStorage(new Date()),
        endDate: '',
        allowMultipleActiveInstances: false,
        concurrencyId: ''
    });

    const executionTypes = useSelectListOptions('api/v1/selectlist/WorkflowExecutionTypes', '-- Ask');

    let idParams: IdParams = useParams();
    let workflowDefinitionId: number | undefined;
    if (idParams.id && !Number.isNaN(parseInt(idParams.id))) {
        workflowDefinitionId = parseInt(idParams.id);
    }

    useEffect(() => {
        if (workflowDefinitionId !== undefined) {
            DataService.getById<WorkflowDefinitionModel>('api/v1/WorkflowDefinitions/', workflowDefinitionId)
                .then(response => setFormData(response))
                .catch(e => {
                    if (e.response?.data?.status === 401)
                        setIsAuthorized(false);
                    else
                        handleException(e);
                });
        }
    }, [workflowDefinitionId]);

    function submit() {
        if (formData) {
            DataService
                .update<WorkflowDefinitionModel>(`api/v1/WorkflowDefinitions/`, formData)
                .then(response => {
                    handleResult(response);
                    setServerErrors(response);
                    if (response.isSuccesful === true && response.data)
                        setFormData(response.data);
                })
                .catch(e => handleException(e));
        }
    }

    const formConfig: FormConfig = {
        fields: [
            { name: 'name', label: 'Name', validationRules: [{ type: 'isRequired' }] },
            { name: 'description', label: 'Description' },
            { name: 'workflowExecutionTypeId', label: 'Execution type' },
            { name: 'projectTypeIds', label: 'Project types' },
            { name: 'itemTypeIds', label: 'Item types' },
            { name: 'beginDate', label: 'Active from', validationRules: [{ type: 'isRequired' }, { type: 'isDate' }] },
            { name: 'endDate', label: 'Active Until', labelSize: 'auto', validationRules: [{ type: 'isDate' }] },
            { name: 'allowMultipleActiveInstances', labelSize: 6, label: 'Allow multiple active instaces' },
            { name: 'isSubItemWorkflow', labelSize: 6, label: 'Sub-Item workflow' },
        ],
        handleSubmit: submit,
        showErrors: 'always',
    };

    const { getLabelProps, getControlProps, getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData, serverErrors);

    const { isAuthorizedFor } = useAuth();
    if (workflowDefinitionId === undefined) {
        if (!isAuthorizedFor(ApplicationFunctions.CreateWorkflowDefinition)) {
            return (<Unauthorized />);
        }
    }
    else if (!isAuthorized) {
        return (<Unauthorized />);
    }

    if (!formData)
        return <SpinnerOverlay />

    const formActions =
        <ActionMenu title="Actions">
            <ActionMenuItem width={230} icon="fa fa-clone" title="Clone workflow" disabled />
            <ActionMenuItem icon="fa fa-trash" iconVariant="danger" title="Delete all instances" disabled />
        </ActionMenu>

    return (
        <div className='main-content'>
            <Form title='Workflow definition' actions={formActions} footer={(
                <Row>
                    <Col md='12'>
                        <ButtonBar offset={2}>
                            <Authorize applicationFunction={ApplicationFunctions.UpdateWorkflowDefinition}>
                                <SaveButton  {...getSubmitButtonProps()} />
                            </Authorize>
                        </ButtonBar>
                    </Col>
                </Row>
            )}>
                <Container fluid>
                    <Row>
                        <Col md='8'>
                            <TextboxField {...getFieldProps('name')} />
                            <TextboxField {...getFieldProps('description')} />
                            <SelectListField {...getFieldProps('workflowExecutionTypeId')} options={executionTypes} />
                            <MultiSelectField {...getFieldProps('projectTypeIds')} options={projectTypes} />
                            <MultiSelectField {...getFieldProps('itemTypeIds')} options={itemTypes} />
                            <Field {...getLabelProps('beginDate')}>
                                <DateControl {...getControlProps('beginDate')} />
                                <span style={{ padding: '7px 15px' }}><FieldLabel {...getLabelProps('endDate')} /></span>
                                <DateControl {...getControlProps('endDate')} />
                            </Field>
                        </Col>
                        <Col md='4'>
                            <SwitchField {...getFieldProps('allowMultipleActiveInstances')} />
                        </Col>

                    </Row>
                </Container>
            </Form>
            {workflowDefinitionId && (
                <Container fluid>
                    <Row>
                        <Col md='12'>
                            <HorizontalTabs
                                id='vtabs'
                                defaultActiveKey='steps'
                                tabs={[
                                    {
                                        title: 'Steps',
                                        eventKey: 'steps',
                                        component: <Workflow_tab_Steps workflowDefinitionId={workflowDefinitionId} ></Workflow_tab_Steps>,
                                    },
                                    {
                                        title: 'Event code tasks',
                                        eventKey: 'items',
                                        component: <Workflow_tab_EventCodeTasks workflowDefinitionId={workflowDefinitionId} ></Workflow_tab_EventCodeTasks>,
                                    },
                                ]}
                            />
                        </Col>
                    </Row>
                </Container>
            )}

        </div>
    );
}
export default WorkflowDefinitionView;